import { useState, useEffect } from "react"
import { axios, useAuth } from "@/contexts/auth"
import Modal from "@/components/modals/Modal"
import { ModalBody, ModalContent, ModalFooter } from "@nextui-org/modal"
import { toast } from "react-toastify"
import { ReactComponent as PlusIcon } from "@/icons/plus-icon-simple.svg"
import { ReactComponent as HeadphoneIcon } from "@/icons/headphone-icon.svg"
import Button from "@/components/Button"
import { Preset } from "@/hooks/presets"
import LoadingSpinner from "@/components/LoadingSpinner"

type Props = {
  isOpen: boolean
  onClose: () => void
  onPresetSelected: (preset: Preset) => void
  createPreset: (preset: {
    name: string
    mode: string
    config: object
    headphone?: {
      id: number
      brand: {
        id: number
        name: string
      }
      model: string
    } | null
    author?: string
    attribution_link?: string
    other_notes?: string
  }) => Promise<Preset | false>
  presetId: number | null
}

const SharedPresetViewModal = ({
  isOpen,
  onClose,
  onPresetSelected,
  createPreset,
  presetId,
}: Props) => {
  const [preset, setPreset] = useState<Preset | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const { isAuthenticated } = useAuth()

  // Fetch preset when modal opens or presetId changes
  useEffect(() => {
    const fetchPreset = async () => {
      if (!presetId) return

      setIsLoading(true)
      try {
        // First, try to fetch the preset directly by ID if the endpoint supports it
        try {
          const directResponse = await axios.get(`/api/equalizer/public-presets/${presetId}/`)
          setPreset(directResponse.data)
          return
        } catch (directError) {
          // If direct fetch fails, fall back to searching through pages
          console.log("Direct fetch failed, searching through pages...")
        }

        // Get the page number from URL parameters as a starting point
        const queryParams = new URLSearchParams(window.location.search)
        const startPage = parseInt(queryParams.get("page") || "1")

        // Search for the preset across multiple pages
        let foundPreset = null
        let currentPage = startPage
        let maxPagesToSearch = 10 // Limit search to avoid infinite loops

        while (!foundPreset && maxPagesToSearch > 0) {
          const response = await axios.get(`/api/equalizer/public-presets/`, {
            params: {
              page: currentPage,
              page_size: 50,
            },
          })

          // Find the preset with matching ID in the current page's results
          foundPreset = response.data.results.find((p) => p.id === presetId)

          if (foundPreset) {
            setPreset(foundPreset)
            return
          }

          // If not found and there are more pages, continue searching
          if (response.data.next) {
            currentPage++
            maxPagesToSearch--
          } else {
            // No more pages to search
            break
          }
        }

        // If still not found, try searching backwards from the starting page
        if (!foundPreset && startPage > 1) {
          currentPage = startPage - 1
          maxPagesToSearch = startPage - 1

          while (!foundPreset && currentPage > 0 && maxPagesToSearch > 0) {
            const response = await axios.get(`/api/equalizer/public-presets/`, {
              params: {
                page: currentPage,
                page_size: 50,
              },
            })

            foundPreset = response.data.results.find((p) => p.id === presetId)

            if (foundPreset) {
              setPreset(foundPreset)
              return
            }

            currentPage--
            maxPagesToSearch--
          }
        }

        // If we get here, the preset wasn't found
        throw new Error("Preset not found")
      } catch (err) {
        console.error("Error fetching preset:", err)
        toast.error("Failed to load preset")
        onClose()
      } finally {
        setIsLoading(false)
      }
    }

    if (isOpen && presetId) {
      fetchPreset()
    }
  }, [isOpen, presetId, onClose])

  const handleListen = (preset) => {
    onPresetSelected(preset)
    // Don't close the modal when listening
  }

  const handleSave = async (preset) => {
    setIsSaving(true)
    try {
      // Create a new preset using the createPreset function from parent
      const newPreset = await createPreset({
        name: `${preset.name} (from ${preset.creator_name})`,
        mode: preset.mode,
        config: preset.config,
        headphone: preset.headphone || null,
      })

      if (newPreset) {
        toast.success("Preset saved successfully")
        // Load the saved preset to the device
        onPresetSelected(newPreset)
        onClose()
      } else {
        throw new Error("Failed to create preset")
      }
    } catch (err) {
      console.error("Error saving preset:", err)
      toast.error("Failed to save preset")
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent className="max-w-[600px] bg-dark/40 backdrop-blur-sm">
        {(onClose) => (
          <>
            <ModalBody className="flex flex-col space-y-4">
              <h2 className="type-body-xl font-bold text-white">Shared Preset ID #{presetId}</h2>

              {isLoading ? (
                <div className="flex h-32 items-center justify-center">
                  <span className="flex items-center gap-2">
                    <LoadingSpinner size="small" />
                    <span className="text-xs text-gray-500">LOADING PRESET</span>
                  </span>
                </div>
              ) : preset ? (
                <div className="flex flex-col rounded-plus border border-[#3D3D3D] bg-[#2B2B2B]/40 p-4 transition-colors duration-200">
                  <div className="flex items-center justify-between">
                    <h2 className="type-body-sm font-medium text-white">{preset.name}</h2>
                  </div>
                  <h3 className="type-body-xs mt-1 font-medium text-silver">
                    {preset.headphone ? (
                      <>
                        {preset.headphone.brand.name} {preset.headphone.model}
                      </>
                    ) : (
                      "No headphone specified"
                    )}
                  </h3>
                  <div className="mt-2 flex items-center gap-x-2">
                    <button
                      className={`type-body-xs ${
                        preset.is_official
                          ? "rounded-full bg-[#FFB800]/20 px-2 py-0.5 font-medium text-[#FFB800]"
                          : "text-[#858484] hover:text-white"
                      } transition-colors`}
                    >
                      by {preset.creator_name}
                    </button>
                  </div>
                  <div className="mt-4 flex items-center gap-x-2">
                    <button
                      className="type-body-xs flex items-center gap-x-1.5 rounded-plus border border-jaguar bg-dark-jaguar/40 px-3 py-1.5 font-medium text-silver transition-colors hover:border-silver hover:text-white"
                      onClick={() => handleListen(preset)}
                    >
                      <HeadphoneIcon className="h-3 w-3" />
                      Listen
                    </button>
                    <button
                      className="type-body-xs flex items-center gap-x-1.5 rounded-plus border border-jaguar bg-dark-jaguar/40 px-3 py-1.5 font-medium text-silver transition-colors hover:border-silver hover:text-white disabled:opacity-50"
                      onClick={() => handleSave(preset)}
                      disabled={isSaving || !isAuthenticated()}
                      title={!isAuthenticated() ? "Please log in to save presets" : undefined}
                    >
                      <PlusIcon className="h-3 w-3" />
                      {isSaving ? "Saving..." : "Save to my Presets"}
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-sm font-normal text-silver">
                  Preset not found or has been removed.
                </p>
              )}
            </ModalBody>
            <ModalFooter className="gap-x-0">
              <Button
                className="-mr-5"
                theme="none"
                size="sm"
                outline={false}
                solid={true}
                full={false}
                onClick={onClose}
              >
                Close
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}

export default SharedPresetViewModal
